// Foundations
import { Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { DialogService } from '@app/services/dialog.service';
import { MyProfileService } from '@app/services/my-profile.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { InterviewRequestService } from '@app/services/interview-request.service';
import {
  AddInterviewRequestGQL,
  AddPetGQL,
} from '@app/services/graphql.service';
import { HelloSignService } from '@app/services/hello-sign.service';

// Models
import { CanDeactivateComponent } from '@app/models/can-deactivate-component.model';

// Components
import { StepOneComponent } from '@app/components/interview-request/step-one/step-one.component';
import { StepTwoComponent } from '@app/components/interview-request/step-two/step-two.component';

// Pipes
import { UnitConversionPipe } from '@app/pipes/unit-conversion.pipe';

@Component({
  selector: 'app-interview-request-page',
  templateUrl: './interview-request-page.component.html',
  styleUrls: ['./interview-request-page.component.scss'],
})
export class InterviewRequestPageComponent
  implements OnInit, CanDeactivateComponent
{
  @HostBinding('class') class = 'page-config';
  @ViewChild('stp1Form', { static: false }) stp1Form: StepOneComponent;
  @ViewChild('stp2Form', { static: false }) stp2Form: StepTwoComponent;
  currIndex = 0;
  enabledTabs = new Set<number>();
  interviewRequestSubmitted = false;

  constructor(
    private route: ActivatedRoute,
    public myProfileService: MyProfileService,
    private helloSignService: HelloSignService,
    private interviewRequestService: InterviewRequestService,
    private _analytics: AnalyticsService,
    private dialogService: DialogService,
    private addInterviewRequestMutation: AddInterviewRequestGQL,
    private addPetMutation: AddPetGQL,
    private loadingIndicatorService: LoadingIndicatorService,
    private convertUnit: UnitConversionPipe,
    private titleService: Title
  ) {
    this.titleService.setTitle('Client Portal - Interview Request');
    this.enabledTabs.add(0);
  }

  canDeactivate() {
    if (this.interviewRequestSubmitted) {
      return true;
    }
    if (
      this.stp1Form &&
      this.stp1Form.form.pristine &&
      !this.stp1Form.form.pristine
    ) {
      return this.dialogService.confirmDiscard();
    } else if (
      this.stp2Form &&
      this.stp2Form.form.pristine &&
      !this.stp2Form.form.pristine
    ) {
      return this.dialogService.confirmDiscard();
    }
    // if the have selected any pets we have form data, this will not be reset on save
    // but that is fine as the data will not be submitted until page 2 anyway
    const petForm1 = this.stp1Form.petFormArray;
    if (petForm1) {
      let sendError = false;
      petForm1.value.forEach((pet) => {
        if (pet.isPetSelected) {
          sendError = true;
        }
      });
      if (sendError) {
        return this.dialogService.confirmDiscard();
      }
    }
    return true;
  }
  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._analytics.logScreen('interviewRequestPage');
    this.interviewRequestService.reset();
    this.myProfileService.fetchLocationList();
    this.helloSignService.fetchForms();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  navigateToTabIndex(tabIndex: number, viaTabContent = false) {
    if (this.enabledTabs.has(tabIndex)) {
      this.currIndex = tabIndex;
      return;
    }
    if (viaTabContent) {
      this.currIndex = tabIndex;
      this.enabledTabs.add(tabIndex);
      return;
    }
    this.currIndex = tabIndex;
  }

  handleRequestSubmitted() {
    const addPetInfo = [];
    const petIds = [];
    const petNotes = [];
    let needAddPet = false;
    this.loadingIndicatorService.show();
    this.stp1Form.petFormArray.controls.forEach((formGroup) => {
      if (formGroup.get('isPetSelected').value) {
        const id = formGroup.get('petId').value;
        const breeds = formGroup.get('breedArr').value;
        const firstName = formGroup.get('firstName').value;
        const lastName = formGroup.get('lastName').value;
        // For new dog, convert from "lb" to "g"
        if (id === -1) {
          const weight = this.convertUnit.transform(
            +formGroup.get('weight').value,
            'lb',
            'g'
          );
          needAddPet = true;
          const breedList = [];
          let isPrimary = true;
          breeds.forEach((breed) => {
            breedList.push({ breed: parseInt(breed.name.id, 10), isPrimary });
            isPrimary = false;
          });
          addPetInfo.push({
            id,
            breeds: breedList,
            firstName,
            lastName,
            weight,
          });
        } else {
          petIds.push(formGroup.get('petId').value);
        }
        if (formGroup.get('petDetails').value !== '') {
          petNotes.push(
            `${firstName} ${lastName}: ${formGroup.get('petDetails').value}`
          );
        }
      }
    });
    if (needAddPet) {
      let numberOfAdds = addPetInfo.length;
      addPetInfo.forEach((pet) => {
        this.addPetMutation
          .mutate({
            petInput: {
              firstName: pet.firstName,
              lastName: pet.lastName,
              weight: pet.weight,
              breeds: pet.breeds,
            },
          })
          .subscribe(
            (res) => {
              if (res.data) {
                petIds.push(res.data.addPet.id);
              }
              numberOfAdds--;
              if (numberOfAdds === 0) {
                this.submitRequest(petIds, petNotes);
              }
            },
            () => {
              this.loadingIndicatorService.hide();
            }
          );
      });
    } else {
      this.submitRequest(petIds, petNotes);
    }
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private submitRequest(petIds: string[], petNotes: string[]): void {
    //
    this.addInterviewRequestMutation
      .mutate({
        interviewRequest: {
          franchiseId: this.interviewRequestService.selectedLocation.id,
          pets: petIds,
          notes: petNotes.join('\r\n'),
        },
      })
      .subscribe(
        (res) => {
          if (res.data) {
            this.interviewRequestSubmitted = true;
            // Reset related data.
            this.interviewRequestService.reset();
          }
          this.loadingIndicatorService.hide();
        },
        () => {
          this.loadingIndicatorService.hide();
        }
      );
  }
}
